import { type ReactNode } from 'react';

import { cn } from '@shared/lib/cn';

export interface BaseFieldProps {
  id: string;
  label: string;
  children: ReactNode;
  error?: string | undefined;
  hint?: ReactNode;
  required?: boolean;
  /** Keep the label for screen readers only (the control shows a placeholder instead). */
  hideLabel?: boolean;
  className?: string;
}

/** Label + control + hint/error, the prototype's stacked field layout. Errors are linked
 *  to the control through `aria-describedby` (`<id>-error`). */
export function BaseField({
  id,
  label,
  children,
  error,
  hint,
  required,
  hideLabel,
  className,
}: BaseFieldProps) {
  return (
    <div className={cn('flex min-w-0 flex-col gap-1', className)}>
      <label htmlFor={id} className={cn('text-[10px] tracking-[0.11em] text-text-2', hideLabel && 'sr-only')}>
        {label}
        {required && (
          <span className="text-crit" aria-hidden>
            {' '}
            *
          </span>
        )}
      </label>
      {children}
      {error ? (
        <span id={`${id}-error`} role="alert" className="text-[11.5px] text-crit normal-case">
          {error}
        </span>
      ) : (
        hint && <span className="text-[11.5px] text-text-3">{hint}</span>
      )}
    </div>
  );
}
